import React from 'react';
import { ShieldCheckIcon, HeartIcon, AwardIcon, UsersIcon, PlayCircleIcon, MapPinIcon } from 'lucide-react';
export function TrustSection() {
  return <section id="why-us" className="w-full bg-soft-teal py-16 lg:py-24" aria-label="Proudly Canadian and Nurse-Owned trust section" data-id="element-670">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-671">
        {/* Section header */}
        <div className="text-center mb-14" data-id="element-672">
          <div className="inline-flex items-center gap-2 bg-white text-medical-blue text-xs font-semibold px-3 py-1.5 rounded-full mb-4 shadow-sm" data-id="element-673">
            🍁 Canadian Owned & Operated
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-text-dark tracking-tight mb-4" data-id="element-674">
            Proudly Canadian &{' '}
            <span className="text-medical-blue" data-id="element-675">Nurse-Owned</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-676">
            Built by nurses, for nurses. Care Nomad was founded by a registered
            nurse who experienced firsthand the broken staffing system — and
            decided to fix it.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16" data-id="element-677">
          {/* Left: Founder video bio */}
          <div className="relative" data-id="element-678">
            <div className="bg-white rounded-2xl shadow-float overflow-hidden" data-id="element-679">
              {/* Video placeholder */}
              <div className="relative bg-gradient-to-br from-medical-blue to-medical-blue-dark aspect-video flex items-center justify-center group cursor-pointer" data-id="element-680">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10" data-id="element-681">
                  <div className="absolute top-4 left-4 w-32 h-32 border-2 border-white rounded-full" data-id="element-682" />
                  <div className="absolute bottom-4 right-4 w-24 h-24 border-2 border-white rounded-full" data-id="element-683" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border border-white rounded-full" data-id="element-684" />
                </div>

                {/* Founder avatar placeholder */}
                <div className="relative z-10 text-center" data-id="element-685">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-white/40" data-id="element-686">
                    <span className="text-white font-bold text-2xl" data-id="element-687">EU</span>
                  </div>
                  <div className="text-white font-bold text-lg" data-id="element-688">Ese Uko</div>
                  <div className="text-white/70 text-sm" data-id="element-689">
                    Co-Founder & Chief Recruitment Officer, Care Nomad
                  </div>
                </div>

                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors" data-id="element-690">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-691">
                    <PlayCircleIcon className="w-8 h-8 text-medical-blue ml-0.5" data-id="element-692" />
                  </div>
                </div>

                {/* Duration badge */}
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-lg font-medium" data-id="element-693">
                  3:42
                </div>
              </div>

              {/* Founder quote */}
              <div className="p-6" data-id="element-694">
                <blockquote className="text-text-dark leading-relaxed italic mb-4" data-id="element-695">
                  "I spent 12 years as a bedside nurse watching my colleagues
                  burn out because agencies treated them like commodities. Care
                  Nomad gives healthcare workers the dignity, flexibility, and
                  pay they deserve — while giving facilities the reliable staff
                  they need."
                </blockquote>
                <div className="flex items-center gap-3" data-id="element-696">
                  <div className="w-10 h-10 bg-soft-teal rounded-full flex items-center justify-center font-bold text-medical-blue text-sm" data-id="element-697">
                    EU
                  </div>
                  <div data-id="element-698">
                    <div className="font-bold text-text-dark text-sm" data-id="element-699">
                      Ese Uko
                    </div>
                    <div className="text-text-muted text-xs" data-id="element-700">
                      Co-Founder · Chief Recruitment Officer
                    </div>
                  </div>
                  <div className="ml-auto" data-id="element-701">
                    <div className="flex items-center gap-1 text-xs text-text-muted" data-id="element-702">
                      <MapPinIcon className="w-3 h-3" data-id="element-703" />
                      Toronto, ON
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Trust pillars */}
          <div className="space-y-5" data-id="element-704">
            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-705">
              <div className="flex items-start gap-4" data-id="element-706">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-707">
                  <ShieldCheckIcon className="w-6 h-6 text-accent-teal" data-id="element-708" />
                </div>
                <div data-id="element-709">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-710">
                    Nurse-Vetted Quality Standard
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-711">
                    Every worker is screened by a registered nurse — not an HR
                    generalist. We verify credentials, check references, and
                    assess clinical competency before anyone joins the platform.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-712">
              <div className="flex items-start gap-4" data-id="element-713">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-714">
                  <HeartIcon className="w-6 h-6 text-accent-teal" data-id="element-715" />
                </div>
                <div data-id="element-716">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-717">
                    Worker-First Philosophy
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-718">
                    Healthcare workers choose their shifts, set their rates, and
                    keep more of what they earn. Better pay and flexibility
                    means better retention and better patient outcomes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-719">
              <div className="flex items-start gap-4" data-id="element-720">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-721">
                  <AwardIcon className="w-6 h-6 text-accent-teal" data-id="element-722" />
                </div>
                <div data-id="element-723">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-724">
                    Regulated & Compliant
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-725">
                    Fully compliant with Ontario's LTCHA, BC's CCALA, and
                    federal healthcare regulations. All workers carry valid
                    CNO/CARNA registration and liability insurance.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-726">
              <div className="flex items-start gap-4" data-id="element-727">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-728">
                  <UsersIcon className="w-6 h-6 text-accent-teal" data-id="element-729" />
                </div>
                <div data-id="element-730">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-731">
                    Community-Driven Growth
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-732">
                    A portion of every transaction funds nursing scholarships
                    and mental health support for healthcare workers. When
                    nurses thrive, communities thrive.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications & logos bar */}
        <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-733">
          <div className="text-center text-xs font-semibold text-text-muted uppercase tracking-widest mb-6" data-id="element-734">
            Recognized & Regulated By
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-735">
            <div className="text-center" data-id="element-736">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-737">
                <span className="text-medical-blue font-bold text-xs" data-id="element-738">CNO</span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-739">
                College of Nurses of Ontario
              </div>
            </div>
            <div className="text-center" data-id="element-740">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-741">
                <span className="text-medical-blue font-bold text-xs" data-id="element-742">
                  CARNA
                </span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-743">
                College & Association of RNs of Alberta
              </div>
            </div>
            <div className="text-center" data-id="element-744">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-745">
                <span className="text-medical-blue font-bold text-xs" data-id="element-746">
                  BCCNM
                </span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-747">
                BC College of Nurses & Midwives
              </div>
            </div>
            <div className="text-center" data-id="element-748">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-749">
                <span className="text-medical-blue font-bold text-xs" data-id="element-750">🍁</span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-751">
                Canadian Owned & Operated
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}